import { Injectable, Injector } from '@angular/core';
import { ShopSettingsService } from '../modules/general/services/shop-settings.service';
import { LoggerService } from './logger/logger.service';
import { LoadingSpinnerService } from './loading-spinner.service';
import { LoginStateService } from '../modules/login/services/login-state.service';
import { PartnerStateService } from '../modules/partner/services/partner-state.service';
import { PriceListStateService } from '../modules/payment/services/pricelist-state.service';
import { SalePriceScheduleStateService } from '../modules/booking/services/sale-price-schedule-state.service';
import { ShopTranslationsService } from '../modules/translations/services/shop-translations.service';
import { ThemeService } from '../modules/general/services/theme.service';
import { GiftCardStepService } from '../modules/giftcard/services/giftcard-step.service';
import { switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BootstrapService {

  private loginStateService: LoginStateService;
  private partnerStateService: PartnerStateService;
  private priceListStateService: PriceListStateService;
  private salePriceScheduleStateService: SalePriceScheduleStateService;
  private shopTranslationsService: ShopTranslationsService;
  private themeService: ThemeService;
  private giftCardStepService: GiftCardStepService;

  constructor(
    private shopSettingsService: ShopSettingsService,
    private logger: LoggerService,
    private loadingSpinnerService: LoadingSpinnerService,
    private injector: Injector,
  ) {
  }

  initialize(): Promise<any> {

    this.loginStateService = this.injector.get(LoginStateService);
    this.shopTranslationsService = this.injector.get(ShopTranslationsService);
    this.themeService = this.injector.get(ThemeService);

    return new Promise((resolve, reject) => {
      of(true)
        .pipe(
          tap(_ => {
            this.logger.info('BootstrapService: initialize..');
            this.loadingSpinnerService.show();
          }),

          tap(_ => { this.logger.info('BootstrapService: ShopTranslationsService initialize..'); }),
          switchMap(_ => this.shopTranslationsService.initialize()),
          tap(_ => { this.logger.info('BootstrapService: ShopTranslationsService initialized'); }),

          tap(_ => { this.logger.info('BootstrapService: ShopSettingsService initialize..'); }),
          switchMap(_ => this.shopSettingsService.initialize()),
          tap(_ => { this.logger.info('BootstrapService: ShopSettingsService initialized'); }),

          tap(_ => { this.logger.info('BootstrapService: ThemeService initialize..'); }),
          switchMap(_ => this.themeService.initialize()),
          tap(_ => { this.logger.info('BootstrapService: ThemeService initialized'); }),

          tap(_ => {
            this.addGTAG();
            this.addGTAGManager();
            this.addFBQ();
          }),

          switchMap(_ => {
            if (this.shopSettingsService.isWebshopMode) {
              return this.webshopServiceInitialize();
            }
            return of(_);
          }),

          tap(_ => { this.logger.info('BootstrapService: LoginStateService initialize..'); }),
          switchMap(_ => this.loginStateService.initialize()),
          tap(_ => { this.logger.info('BootstrapService: LoginStateService initialized'); }),

          tap(_ => { this.logger.info('BootstrapService: PartnerStateService initialize..'); }),
          switchMap(_ => {
            this.partnerStateService = this.injector.get(PartnerStateService);
            return this.partnerStateService.initialize();
          }),
          tap(_ => { this.logger.info('BootstrapService: PartnerStateService initialized'); }),
        )
        .subscribe(
          _ => {
            this.logger.info('BootstrapService: initialized.');
            this.loadingSpinnerService.hide();
            resolve(true);
          },
          error => {
            this.logger.error(`BootstrapService: initialization error: ${error.message}.`);
            this.loadingSpinnerService.hide();
            reject(false);
          }
        );
    });
  }

  webshopServiceInitialize(): Observable<any> {
    return of(true).pipe(
      tap(_ => { this.logger.info('BootstrapService: PriceListStateService initialize..'); }),
      switchMap(_ => {
        this.priceListStateService = this.injector.get(PriceListStateService);
        return this.priceListStateService.initialize();
      }),
      tap(_ => { this.logger.info('BootstrapService: PriceListStateService initialized'); }),

      tap(_ => { this.logger.info('BootstrapService: SalePriceScheduleStateService initialize..'); }),
      switchMap(_ => {
        this.salePriceScheduleStateService = this.injector.get(SalePriceScheduleStateService);
        return this.salePriceScheduleStateService.initialize();
      }),
      tap(_ => { this.logger.info('BootstrapService: SalePriceScheduleStateService initialized'); }),

      tap(_ => { this.logger.info('BootstrapService: GiftCardStateService initialize..'); }),
      switchMap(_ => {
        this.giftCardStepService = this.injector.get(GiftCardStepService);
        return this.giftCardStepService.reinitialize();
      }),
      tap(_ => { this.logger.info('BootstrapService: GiftCardStateService initialized'); }),
    );
  }

  addGTAG(): void {
    if (this.shopSettingsService.googleAnalyticsKey) {
      const gaScript = document.createElement('script');
      gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.shopSettingsService.googleAnalyticsKey;
      // gaScript.onload = function() {
      //   console.log('gaScript loaded and ready');
      // };
      document.head.appendChild(gaScript);
      const gaInit = document.createElement('script');
      gaInit.setAttribute('type', 'text/javascript');
      gaInit.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments)};
      gtag('js', new Date());
      gtag('config', '${this.shopSettingsService.googleAnalyticsKey}', {
         'anonymize_ip': true,
         'send_page_view': false
        });
      // gtag('event', 'page_view', { 'send_to': '${this.shopSettingsService.googleAnalyticsKey}' });
      `;
      document.head.appendChild(gaInit);
    }
  }

  addGTAGManager(): void {
    if (this.shopSettingsService.gtagGlobalManagerKey) {
      const gaScript = document.createElement('script');
      gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.shopSettingsService.gtagGlobalManagerKey;
      // gaScript.onload = function() {
      //   console.log('gaScript loaded and ready');
      // };
      document.head.appendChild(gaScript);
      const gaInit = document.createElement('script');
      gaInit.setAttribute('type', 'text/javascript');
      gaInit.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtagmanager(){dataLayer.push(arguments)};
      gtagmanager('js', new Date());
      gtagmanager('config', '${this.shopSettingsService.gtagGlobalManagerKey}', {
         'anonymize_ip': true,
         'send_page_view': false
        });
      // gtagmanager('event', 'page_view', { 'send_to': '${this.shopSettingsService.gtagGlobalManagerKey}' });
      `;
      document.head.appendChild(gaInit);
    }
  }

  addFBQ(): void {
    if (this.shopSettingsService.facebookAnalyticsId) {
      const fbqInit = document.createElement('script');
      fbqInit.setAttribute('type', 'text/javascript');

      fbqInit.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq.disablePushState = true;
      fbq.allowDuplicatePageViews = true;
      fbq('init', '${this.shopSettingsService.facebookAnalyticsId}');
      // fbq('track', 'PageView');
      `;

      document.head.appendChild(fbqInit);
    }
  }
}
